// 門鈴多端接聽（docs/panel SD §7.2-4、§7 末 v1.5；05-ui-ux V-14）。
//   - 收：signaling 廣播 {type:"doorbell_answered", device_id, ring_id, answered_by:{client:"panel",panel_device_id}|{client:"app",user_id}}
//     → 前景顯示「○○ 已接聽」（仍可看門口）。只有主人帳號下的連線收得到。
//   - 送：門口即時影像開起來後，按［接聽］送 {type:"doorbell_answered", device_id, ring_id, handoff_id?}
//     → response status ok | already_answered（帶 answered_by）| error。
import type { SignalFrame } from '../../lib/ws';

export interface AnsweredBy {
  client: 'panel' | 'app' | string;
  panel_device_id?: string;
  user_id?: string;
}

export interface Answered {
  deviceId: string;
  ringId: string;
  by: AnsweredBy;
  at: number;
}

/** 顯示多久（一通門鈴的有效期間大概 2 分鐘） */
export const ANSWERED_TTL_MS = 120_000;

export function parseAnsweredFrame(f: SignalFrame): Answered | null {
  if (f.type !== 'doorbell_answered') return null;
  const deviceId = typeof f.device_id === 'string' ? f.device_id : '';
  const ringId = typeof f.ring_id === 'string' ? f.ring_id : '';
  const by = (f.answered_by ?? null) as AnsweredBy | null;
  if (!deviceId || !by || typeof by.client !== 'string') return null;
  return { deviceId, ringId, by, at: Date.now() };
}

/** 「○○ 已接聽」：面板 → 面板名稱；App → 家人（自己 → 你） */
export function answeredText(
  t: (k: string, o?: Record<string, unknown>) => string,
  by: AnsweredBy,
  names: Map<string, string>,
  myUserId: string | null,
): string {
  if (by.client === 'panel') {
    const name = (by.panel_device_id && names.get(by.panel_device_id)) || t('doorbell.answeredPanelFallback');
    return t('doorbell.answeredPanel', { name });
  }
  if (by.user_id && myUserId && by.user_id === myUserId) return t('doorbell.answeredMe');
  return t('doorbell.answeredApp');
}

export type AnswerOutcome = { status: 'ok' } | { status: 'already_answered'; by: AnsweredBy | null } | { status: 'error'; message: string };

export function answerOutcome(resp: SignalFrame): AnswerOutcome {
  const st = String(resp.status ?? (resp.type === 'error' ? 'error' : 'ok'));
  if (st === 'ok') return { status: 'ok' };
  if (st === 'already_answered') return { status: 'already_answered', by: (resp.answered_by ?? null) as AnsweredBy | null };
  return { status: 'error', message: String(resp.message ?? st) };
}

export function answerFrame(deviceId: string, ringId: string, handoffId?: string): SignalFrame {
  return { type: 'doorbell_answered', device_id: deviceId, ring_id: ringId, ...(handoffId ? { handoff_id: handoffId } : {}) };
}

// ---- store：deviceId → 最近一次「別端已接聽」 ----
const byDevice = new Map<string, Answered>();
/** deviceId → 名稱（AppShell 拉 /v3/devices 時填；門鈴卡顯示面板名稱用） */
const names = new Map<string, string>();
const subs = new Set<() => void>();
let version = 0;

export const doorbellAnswered = {
  /** 2 分鐘內的才算 */
  get(deviceId: string, now = Date.now()): Answered | null {
    const a = byDevice.get(deviceId);
    return a && now - a.at < ANSWERED_TTL_MS ? a : null;
  },
  put(a: Answered): void {
    byDevice.set(a.deviceId, a);
    version++;
    for (const fn of subs) fn();
  },
  names(): Map<string, string> {
    return names;
  },
  setName(deviceId: string, name: string): void {
    names.set(deviceId, name);
  },
  version(): number {
    return version;
  },
  subscribe(fn: () => void): () => void {
    subs.add(fn);
    return () => {
      subs.delete(fn);
    };
  },
  /** signaling 'message' 監聽器（AppShell 掛一次） */
  onFrame(f: SignalFrame): void {
    const a = parseAnsweredFrame(f);
    if (a) doorbellAnswered.put(a);
  },
};

export function __resetAnsweredForTest(): void {
  byDevice.clear();
  names.clear();
  subs.clear();
  version = 0;
}
